import {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
	Fragment,
} from "react";
import { supabase } from "@/lib/supabase";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getExpandedRowModel,
	getSortedRowModel,
	getPaginationRowModel,
	flexRender,
	Row,
	ColumnSizingState,
	SortingState,
	PaginationState,
} from "@tanstack/react-table";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../components/ui/table";
import { cn } from "@/dentlab/src/lib/utils";
import { Button } from "../../../components/ui/button";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { EmptyDataPlaceholder } from "@/components/src/empty-with-label/empty-data-placeholder.component";
import { FileViewerModal } from "@components/file-viewer";
import { InvoiceContextMenu } from "./invoice-context-menu.component";
import { PaymentContextMenu } from "./payment-context-menu.component";
import { BookRemainderAsLossDialog } from "./book-remainder-as-loss-dialog.component";
import { InvoiceJobDocumentsDialog } from "./invoice-job-documents-dialog.component";
import { InvoiceShareEmailDialog } from "../../../components/invoice-share-email-dialog/invoice-share-email-dialog.component";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../components/ui/select";
import type {
	ContextMenuData,
	TablePaymentRow,
	TableInvoiceRow,
	PaymentDetail,
	InvoiceReminderActions,
} from "../types/invoice-table.types";
import { getPdfTemplateType } from "../types/invoice-table.types";
import { InvoiceType } from "../../../types/database-enums/invoices-enums";
import type {
	PaymentJournalTableRowData,
	InvoiceAllocationSubRow,
	PaymentJournalRowWithSubRows,
} from "../types/payment-journal.types";
import {
	isInvoiceSubRow,
	isPaymentPrimaryRow,
} from "../types/payment-journal.types";
import { PaymentType } from "../../../types/database-enums/payment-enums";
import { useAccountingStore } from "../../../store/Accounting";
import { showNotification } from "../../../store/Central/selectors";
import {
	useStorage,
	uploadFileWithProvidedPath,
} from "../../../hooks/useStorage";
import {
	dispatchPrint,
	getResolvedPrintMode,
} from "../../../lib/utils/print-dispatch";
import { addCopyWatermark } from "../../../lib/utils/pdf-watermark";
import { v4 as uuidv4 } from "uuid";
import { buildPaymentRowColumns, PaymentColumnDef } from "./table-columns";
import {
	createExpanderColumn,
	createInvoiceNumberColumn,
	createRecipientColumn,
	createClientColumn,
	createSharingColumn,
	createInvoiceAmountColumn,
	createCurrencyColumn,
	createInvoiceDateColumn,
	createCancellationDateColumn,
	createCancellationReasonColumn,
	createPaymentStatusColumn,
	createActionsColumn,
	createZakStatusColumn,
	createVATRateColumn,
	createVATNetAmountColumn,
	createVATAmountColumn,
	createVATGrossAmountColumn,
	createPaymentPercentageColumn,
	createOpenAmountColumn,
	createPaymentExpanderColumn,
	createPaymentJournalValueColumn,
	createPaymentJournalTypeColumn,
	createPaymentJournalValutaDateColumn,
	createPaymentJournalSenderColumn,
	createPaymentJournalNoteColumn,
	buildInvoiceAllocationSubRowColumns,
	InvoiceAllocationColumnDef,
} from "./table-columns";
import {
	calculateVATBreakdown,
	calculateVATGrandTotals,
	VATCalculationMode,
} from "../utils/vat-calculations";
import { formatCurrency, formatPercentage } from "./table-columns";
import { Logger } from "@/lib/logger/Logger";

// Page size options for pagination
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500, 1000];

export interface TableRowData {
	isPaymentRow?: boolean;
	subRows?: TablePaymentRow[];
}

// Type guard functions
function isPaymentRow(row: TableRowData): row is TablePaymentRow {
	return row.isPaymentRow === true;
}

function isInvoiceRow(row: TableRowData): row is TableInvoiceRow {
	return !row.isPaymentRow;
}

// UI callback functions interface
export interface UICallbacks {
	onPaymentDialogOpen?: (invoice: TableInvoiceRow) => void;
	onCancellationDialogOpen?: (invoice: TableInvoiceRow) => void;
	onRefreshData?: () => Promise<void>;
	reminderActions?: InvoiceReminderActions;
}

// Feature configuration interface
export interface TableFeatures {
	allowSharing?: boolean;
	allowCancelInvoice?: boolean;
	allowPayments?: boolean;
	allowBookAsLoss?: boolean;
	readOnlyMode?: boolean;
	historicalView?: boolean;
	allowZak?: boolean;
}

export interface AbstractInvoiceTableProps<T extends TableRowData> {
	data: T[];
	loading?: boolean;
	highlightedRows?: number[];

	// Table configuration
	tableType?: "active" | "canceled" | "payment-journal" | "open-items";

	// UI callbacks
	uiCallbacks?: UICallbacks;

	// Feature flags
	features?: TableFeatures;

	// Table state
	sorting?: SortingState;
	onSortingChange?: (sorting: SortingState) => void;
	pagination?: PaginationState;
	onPaginationChange?: (pagination: PaginationState) => void;
	globalFilter?: string;
	onGlobalFilterChange?: (filter: string) => void;

	// Data fetching
	totalRowCount?: number;
	manualSorting?: boolean;
	manualPagination?: boolean;
	manualFiltering?: boolean;

	// VAT Summary
	showVATSummary?: boolean;

	// Payment Summary
	showPaymentSummary?: "open" | "paid" | false;

	// Empty state
	emptyStateLabel?: string;
}

export function AbstractInvoiceTable<T extends TableRowData>({
	data,
	loading = false,
	highlightedRows = [],
	tableType = "active",
	uiCallbacks,
	features,
	sorting,
	onSortingChange,
	pagination,
	onPaginationChange,
	globalFilter,
	onGlobalFilterChange,
	totalRowCount,
	manualSorting = false,
	manualPagination = false,
	manualFiltering = false,
	showVATSummary = false,
	showPaymentSummary = false,
	emptyStateLabel = "Keine Daten vorhanden",
}: AbstractInvoiceTableProps<T>) {
	// FileViewer state
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	// Book Remainder as Loss Dialog state
	const [lossBookingDialogOpen, setLossBookingDialogOpen] = useState(false);
	const [selectedInvoiceForLoss, setSelectedInvoiceForLoss] =
		useState<TableInvoiceRow | null>(null);
	const [lossBookingLoading, setLossBookingLoading] = useState(false);

	// Job Documents Dialog state
	const [jobDocumentsDialogOpen, setJobDocumentsDialogOpen] = useState(false);
	const [selectedInvoiceForJobDocuments, setSelectedInvoiceForJobDocuments] =
		useState<TableInvoiceRow | null>(null);

	const [emailDialogData, setEmailDialogData] = useState<{
		open: boolean;
		invoice: TableInvoiceRow | null;
		recipient: string;
	}>({ open: false, invoice: null, recipient: "" });

	const resetEmailDialog = () => {
		setEmailDialogData({ open: false, invoice: null, recipient: "" });
	};

	// Import store functions directly
	const {
		shareInvoice,
		unshareInvoice,
		cancelInvoice,
		createPayment,
		cancelPayment,
		cancelLossAllocation,
		rebookPaymentAsAkonto,
	} = useAccountingStore((state) => ({
		shareInvoice: state.shareInvoice,
		unshareInvoice: state.unshareInvoice,
		cancelInvoice: state.cancelInvoice,
		createPayment: state.createPayment,
		cancelPayment: state.cancelPayment,
		cancelLossAllocation: state.cancelLossAllocation,
		rebookPaymentAsAkonto: state.rebookPaymentAsAkonto,
	}));

	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
	const [contextMenu, setContextMenu] = useState<
		ContextMenuData<TableRowData>
	>({
		visible: false,
		x: 0,
		y: 0,
		row: null,
	});
	const tableRef = useRef<HTMLDivElement>(null);
	const [summaryWidth, setSummaryWidth] = useState<number | undefined>(
		undefined
	);

	// File opening handler for FileViewer
	const handleOpenFile = useCallback((fileId: string) => {
		setSelectedFileId(fileId);
		setIsViewerOpen(true);
	}, []);

	// File opening handler for printing - uses desktop or browser mode
	const { fetchFileFromPath } = useStorage();
	const handleOpenFileInNewTab = useCallback(
		async (
			pathName: string,
			invoiceType?: number | null,
			pdfTemplateType?: string | null
		) => {
			const { success, data: signedUrl } =
				await fetchFileFromPath(pathName);
			if (!success || !signedUrl) {
				showNotification({
					message: "Fehler beim Öffnen der Datei",
					type: "error",
				});
				return;
			}
			if (getResolvedPrintMode() === "desktop") {
				const docTypeMap: Record<number, string> = {
					0: "rg",
					1: "bar",
					2: "akonto",
					11: "karte",
					20: "srg",
				};
				const docType =
					invoiceType === InvoiceType.MAHNGEBUEHR && pdfTemplateType
						? pdfTemplateType
						: docTypeMap[invoiceType ?? 0] ?? "rg";
				const res = await fetch(signedUrl);
				const blob = await res.blob();
				await dispatchPrint({
					documentType: docType,
					documentData: blob,
					fileName: pathName.split("/").pop() ?? "Rechnung.pdf",
				});
			} else {
				const res = await fetch(signedUrl);
				const blob = await res.blob();
				const blobUrl = URL.createObjectURL(blob);
				const w = window.open(blobUrl, "_blank");
				if (w) {
					w.focus();
					w.print();
					setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
				}
			}
		},
		[fetchFileFromPath]
	);

	const handlePrintAsCopy = useCallback(
		async (invoice: TableInvoiceRow) => {
			if (!invoice.invoice_id || !invoice.path_name) {
				showNotification({
					message: "Rechnung nicht verfügbar",
					type: "error",
				});
				return;
			}

			const { data: invoiceRow, error: invoiceErr } = await supabase
				.from(SupabaseTableEnum.INVOICES)
				.select(
					"copy_file_id, copy_file:files!invoices_copy_file_id_fkey(path_name)"
				)
				.eq("id", invoice.invoice_id)
				.maybeSingle();

			if (invoiceErr) {
				showNotification({
					message: "Fehler beim Laden der Rechnung",
					type: "error",
				});
				return;
			}

			const existingCopyPath = (
				invoiceRow?.copy_file as { path_name: string } | null
			)?.path_name;
			if (existingCopyPath) {
				await handleOpenFileInNewTab(
					existingCopyPath,
					invoice.invoice_type,
					getPdfTemplateType(invoice)
				);
				return;
			}

			const { success: fetchSuccess, data: signedUrl } =
				await fetchFileFromPath(invoice.path_name);
			if (!fetchSuccess || !signedUrl) {
				showNotification({
					message: "Originaldatei konnte nicht geladen werden",
					type: "error",
				});
				return;
			}

			const originalRes = await fetch(signedUrl);
			const originalBlob = await originalRes.blob();
			const copyBlob = await addCopyWatermark(originalBlob);

			const originalName =
				invoice.path_name.split("/").pop() ?? "Rechnung.pdf";
			const copyFileName = `Kopie_${originalName}`;
			const copyPath = `${uuidv4()}/${copyFileName}`;

			const { success: uploadSuccess } = await uploadFileWithProvidedPath(
				{
					filePath: copyPath,
					fileBody: copyBlob,
					fileOptions: { contentType: "application/pdf" },
				}
			);
			if (!uploadSuccess) {
				showNotification({
					message: "Kopie konnte nicht gespeichert werden",
					type: "error",
				});
				return;
			}

			const { error: rpcErr } = await supabase.rpc(
				"set_invoice_copy_file",
				{
					p_invoice_id: invoice.invoice_id,
					p_path_name: copyPath,
					p_file_name: copyFileName,
				}
			);
			if (rpcErr) {
				showNotification({
					message: "Kopie konnte nicht registriert werden",
					type: "error",
				});
				return;
			}

			await handleOpenFileInNewTab(
				copyPath,
				invoice.invoice_type,
				getPdfTemplateType(invoice)
			);
		},
		[fetchFileFromPath, handleOpenFileInNewTab]
	);

	// Transform invoice data to include payment sub-rows
	// For payment-journal, data already has correct subRows from zahlungsjournal.page.tsx
	const tableData = useMemo<T[]>(() => {
		if (!data || !Array.isArray(data)) {
			return [];
		}

		// Payment-journal: preserve existing subRows structure (already transformed)
		if (tableType === "payment-journal") {
			return data.filter((item) => item != null) as T[];
		}

		// Other tables: transform payments to subRows
		return data
			.filter((item) => item != null)
			.map((item) => ({
				...item,
				isPaymentRow: false as const,
				subRows: (
					(item as any).payments as unknown as PaymentDetail[]
				)?.map((payment: PaymentDetail) => ({
					...payment,
					invoice_id: (item as any).invoice_id || 0,
					isPaymentRow: true as const,
				})),
			})) as T[];
	}, [data, tableType]);

	// Collect all file IDs from invoices for gallery
	const allFileIds = useMemo(() => {
		return tableData
			.filter((row) => isInvoiceRow(row) && row.file_id)
			.map((row) => (row as any).file_id)
			.filter(Boolean) as string[];
	}, [tableData]);

	useEffect(() => {
		if (!tableRef.current) return;

		const updateWidth = () => {
			if (tableRef.current) {
				setSummaryWidth(tableRef.current.clientWidth);
			}
		};

		updateWidth();
		const resizeObserver = new ResizeObserver(() => updateWidth());
		resizeObserver.observe(tableRef.current);

		return () => resizeObserver.disconnect();
	}, []);

	const summaryData = useMemo(() => {
		if (!showVATSummary && !showPaymentSummary) {
			return null;
		}

		const invoicesOnly = tableData.filter((row) =>
			isInvoiceRow(row)
		) as unknown as TableInvoiceRow[];

		if (!invoicesOnly.length) {
			return null;
		}

		const mode: VATCalculationMode =
			showPaymentSummary === "open"
				? "open"
				: showPaymentSummary === "paid"
					? "paid"
					: "full";

		const invoicesByCurrency = invoicesOnly.reduce<
			Record<string, TableInvoiceRow[]>
		>((acc, invoice) => {
			const currency = invoice.invoice_currency || "CHF";
			if (!acc[currency]) {
				acc[currency] = [];
			}
			acc[currency].push(invoice);
			return acc;
		}, {});

		const rows: {
			currency: string;
			taxRate: number;
			invoiceCount: number;
			netAmount: number;
			vatAmount: number;
			grossAmount: number;
		}[] = [];

		const currencyTotals: {
			currency: string;
			invoiceCount: number;
			netAmount: number;
			vatAmount: number;
			grossAmount: number;
		}[] = [];

		Object.entries(invoicesByCurrency).forEach(([currency, invoices]) => {
			const breakdown = calculateVATBreakdown(invoices, mode);
			const totals = calculateVATGrandTotals(breakdown);

			breakdown.forEach((entry) => {
				rows.push({
					currency,
					taxRate: entry.taxRate,
					invoiceCount: entry.invoiceCount,
					netAmount: entry.netAmount,
					vatAmount: entry.vatAmount,
					grossAmount: entry.grossAmount,
				});
			});

			currencyTotals.push({
				currency,
				invoiceCount: totals.invoiceCount,
				netAmount: totals.netAmount,
				vatAmount: totals.vatAmount,
				grossAmount: totals.grossAmount,
			});
		});

		return {
			rows,
			currencyTotals,
			showTotals: rows.length > 1,
			mode,
		};
	}, [tableData, showVATSummary, showPaymentSummary]);

	const summaryTitle =
		tableType === "payment-journal"
			? "Total - Zahlungseingänge"
			: showPaymentSummary === "open"
				? "Total - Offene Beträge"
				: showPaymentSummary === "paid"
					? "Total - Bezahlte Beträge"
					: "Total - Rechnungsbeträge";

	// Payment-journal specific summary (payments grouped by currency and VAT rate)
	const paymentJournalSummary = useMemo(() => {
		if (tableType !== "payment-journal") return null;

		const payments = tableData.filter(
			(row) =>
				"payment_id" in row &&
				!("isInvoiceSubRow" in row && row.isInvoiceSubRow)
		) as unknown as PaymentJournalRowWithSubRows[];

		if (!payments.length) return null;

		// Group by currency and VAT rate from invoice allocations
		// Use string key to handle special cases like "akonto" for pseudo invoices
		const byCurrencyAndRate: Record<
			string,
			Record<
				string,
				{
					allocationCount: number;
					grossAmount: number;
					vatAmount: number;
					netAmount: number;
					isAkonto: boolean;
				}
			>
		> = {};

		payments.forEach((payment) => {
			const allocations = payment.invoice_allocations || [];
			allocations.forEach((allocation) => {
				if (allocation.allocation_is_canceled) return;

				const currency =
					allocation.invoice_currency ||
					payment.payment_currency ||
					"CHF";
				const allocatedAmount = allocation.allocated_amount || 0;

				// Check if this is a pseudo akonto invoice (type 99)
				const isPseudoAkonto = allocation.invoice_type === 99;

				// For akonto, use special key; otherwise use tax rate
				const rateKey = isPseudoAkonto
					? "akonto"
					: String(allocation.invoice_effective_tax_rate || 0);
				const taxRate = isPseudoAkonto
					? 0
					: allocation.invoice_effective_tax_rate || 0;

				// Calculate proportional VAT for this allocation (0 for akonto)
				const vatAmount =
					taxRate > 0
						? allocatedAmount * (taxRate / (100 + taxRate))
						: 0;
				const netAmount = allocatedAmount - vatAmount;

				if (!byCurrencyAndRate[currency]) {
					byCurrencyAndRate[currency] = {};
				}
				if (!byCurrencyAndRate[currency][rateKey]) {
					byCurrencyAndRate[currency][rateKey] = {
						allocationCount: 0,
						grossAmount: 0,
						vatAmount: 0,
						netAmount: 0,
						isAkonto: isPseudoAkonto,
					};
				}

				byCurrencyAndRate[currency][rateKey].allocationCount += 1;
				byCurrencyAndRate[currency][rateKey].grossAmount +=
					allocatedAmount;
				byCurrencyAndRate[currency][rateKey].vatAmount += vatAmount;
				byCurrencyAndRate[currency][rateKey].netAmount += netAmount;
			});
		});

		// Count unique payments per currency (for totals row)
		const paymentCountByCurrency: Record<string, number> = {};
		payments.forEach((payment) => {
			const currency = payment.payment_currency || "CHF";
			paymentCountByCurrency[currency] =
				(paymentCountByCurrency[currency] || 0) + 1;
		});

		// Build summary rows grouped by currency
		const rows: {
			currency: string;
			taxRate: number;
			count: number;
			netAmount: number;
			vatAmount: number;
			grossAmount: number;
			isAkonto: boolean;
		}[] = [];

		const currencyTotals: {
			currency: string;
			paymentCount: number;
			netAmount: number;
			vatAmount: number;
			grossAmount: number;
		}[] = [];

		Object.entries(byCurrencyAndRate).forEach(([currency, byRate]) => {
			let totalNet = 0;
			let totalVat = 0;
			let totalGross = 0;

			// Sort: regular rates first (numerically), then akonto at the end
			Object.entries(byRate)
				.sort(([a, dataA], [b, dataB]) => {
					if (dataA.isAkonto && !dataB.isAkonto) return 1;
					if (!dataA.isAkonto && dataB.isAkonto) return -1;
					return Number(a) - Number(b);
				})
				.forEach(([rate, data]) => {
					rows.push({
						currency,
						taxRate: data.isAkonto ? -1 : Number(rate), // Use -1 as marker for akonto
						count: data.allocationCount,
						netAmount: data.netAmount,
						vatAmount: data.vatAmount,
						grossAmount: data.grossAmount,
						isAkonto: data.isAkonto,
					});
					totalNet += data.netAmount;
					totalVat += data.vatAmount;
					totalGross += data.grossAmount;
				});

			currencyTotals.push({
				currency,
				paymentCount: paymentCountByCurrency[currency] || 0,
				netAmount: totalNet,
				vatAmount: totalVat,
				grossAmount: totalGross,
			});
		});

		return { rows, currencyTotals };
	}, [tableData, tableType]);

	// Unified summary data structure for both invoice and payment journal views
	const unifiedSummary = useMemo(() => {
		// Payment journal uses its own summary
		if (
			tableType === "payment-journal" &&
			paymentJournalSummary &&
			paymentJournalSummary.rows.length > 0
		) {
			const rowsByCurrency = paymentJournalSummary.rows.reduce<
				Record<string, typeof paymentJournalSummary.rows>
			>((acc, row) => {
				if (!acc[row.currency]) acc[row.currency] = [];
				acc[row.currency].push(row);
				return acc;
			}, {});

			return {
				groups: paymentJournalSummary.currencyTotals.map((total) => ({
					currency: total.currency,
					entries: (rowsByCurrency[total.currency] || []).map(
						(row) => ({
							currency: row.currency,
							taxRate: row.taxRate,
							count: row.count,
							netAmount: row.netAmount,
							vatAmount: row.vatAmount,
							grossAmount: row.grossAmount,
							isAkonto: row.isAkonto,
						})
					),
					total: {
						count: total.paymentCount,
						netAmount: total.netAmount,
						vatAmount: total.vatAmount,
						grossAmount: total.grossAmount,
					},
				})),
				countLabel: "Anzahl",
			};
		}

		// Invoice-based summary for other views
		if (summaryData && summaryData.rows.length > 0) {
			const rowsByCurrency = summaryData.rows.reduce<
				Record<string, typeof summaryData.rows>
			>((acc, row) => {
				if (!acc[row.currency]) acc[row.currency] = [];
				acc[row.currency].push(row);
				return acc;
			}, {});

			return {
				groups: summaryData.currencyTotals.map((total) => ({
					currency: total.currency,
					entries: (rowsByCurrency[total.currency] || []).map(
						(row) => ({
							currency: row.currency,
							taxRate: row.taxRate,
							count: row.invoiceCount,
							netAmount: row.netAmount,
							vatAmount: row.vatAmount,
							grossAmount: row.grossAmount,
							isAkonto: false,
						})
					),
					total: {
						count: total.invoiceCount,
						netAmount: total.netAmount,
						vatAmount: total.vatAmount,
						grossAmount: total.grossAmount,
					},
				})),
				countLabel: "Rechnungen",
			};
		}

		return null;
	}, [tableType, paymentJournalSummary, summaryData]);

	const netLabel =
		showPaymentSummary === "open"
			? "Netto (offen)"
			: showPaymentSummary === "paid"
				? "Netto (bezahlt)"
				: "Netto";

	const grossLabel =
		showPaymentSummary === "open"
			? "Brutto (offen)"
			: showPaymentSummary === "paid"
				? "Brutto (bezahlt)"
				: "Brutto";

	const vatLabel =
		showPaymentSummary === "open"
			? "MwSt (offen)"
			: showPaymentSummary === "paid"
				? "MwSt (bezahlt)"
				: "MwSt";

	const summaryContainerStyle = summaryWidth
		? { width: summaryWidth }
		: undefined;

	const handleShareToggle = useCallback(
		async (invoice: TableInvoiceRow) => {
			if (!shareInvoice || !unshareInvoice) return;

			const invoiceId = invoice.invoice_id;
			if (!invoiceId) return;

			if (invoice.invoice_type === InvoiceType.MAHNGEBUEHR) {
				showNotification({
					message:
						"Mahngebühren werden mit der Mahnung unter Debitoren → Mahnwesen geteilt.",
					type: "info",
				});
				return;
			}

			const isCurrentlyShared =
				invoice.shared_at &&
				(invoice.shared_until == null ||
					new Date(invoice.shared_until) > new Date());

			if (isCurrentlyShared) {
				unshareInvoice(invoiceId);
			} else {
				const result = await shareInvoice(invoiceId);

				if (!result.success) {
					Logger.info(
						"Skipping email popup because result.success is false"
					);
					// notification is handled in shareInvoice
					return;
				}

				if (!result.invoiceData) {
					showNotification({
						message:
							"Die Rechnung wurde geteilt, aber es gab einen Fehler bei der Vorbereitung der E-Mail. Bitte kontaktieren Sie den Support.",
						type: "error",
					});
					return;
				}

				// If the client is not connected, there is no point in showing the email popup
				if (!invoice.client_connected_organization_id) {
					Logger.info(
						"Skipping email popup because invoice.client_connected_organization_id is null"
					);
					return;
				}

				const { data } = await supabase
					.from(SupabaseTableEnum.PROFILES)
					.select("email")
					.eq(
						"organization_id",
						invoice.client_connected_organization_id
					)
					.order("created_at", { ascending: true }) // Uses owner email
					.limit(1)
					.single(); //TODO Should use profilesForChat lookup instead
				const recipientEmail = data?.email ?? "";
				if (!recipientEmail) {
					Logger.info(
						"Skipping email popup because recipientEmail is null"
					);
					showNotification({
						message:
							"Die Rechnung wurde geteilt, aber die E-Mail des Kunden konnte nicht gefunden werden. Bitte kontaktieren Sie den Support.",
						type: "error",
					});
					return;
				}

				setEmailDialogData({
					open: true,
					invoice: result.invoiceData,
					recipient: recipientEmail,
				});
			}
		},
		[shareInvoice, unshareInvoice]
	);

	// BOOK REMAINDER AS LOSS - Opens dialog for user input
	const handleBookRemainderAsLoss = useCallback(
		(invoice: TableInvoiceRow) => {
			setSelectedInvoiceForLoss(invoice);
			setLossBookingDialogOpen(true);
		},
		[]
	);

	// Handle loss booking confirmation from dialog
	const handleConfirmLossBooking = useCallback(
		async (lossComment: string) => {
			if (!createPayment || !selectedInvoiceForLoss) return;

			setLossBookingLoading(true);

			try {
				const payments = selectedInvoiceForLoss.payments as
					| PaymentDetail[]
					| null;
				const totalAllocated =
					payments?.reduce((sum, payment) => {
						const paymentValid =
							payment.payment_id !== null &&
							payment.payment_canceled_at === null &&
							payment.allocation_canceled_at === null;
						return paymentValid
							? sum + (payment.allocated_amount || 0)
							: sum;
					}, 0) || 0;

				const remainingAmount = Math.max(
					0,
					(selectedInvoiceForLoss.invoice_value || 0) - totalAllocated
				);

				if (remainingAmount > 0 && selectedInvoiceForLoss.invoice_id) {
					await createPayment({
						invoiceId: selectedInvoiceForLoss.invoice_id,
						amount: 0,
						valutaDate: new Date().toISOString(),
						paymentType: PaymentType.BARZAHLUNG,
						note: "Restbetrag als Verlust gebucht",
						bookRemainderAsLoss: true,
						generatePaymentSlip: false,
						lossComment: lossComment,
					});

					// Refresh data if callback provided
					if (uiCallbacks?.onRefreshData) {
						await uiCallbacks.onRefreshData();
					}

					showNotification({
						message: `Restbetrag von CHF ${remainingAmount.toFixed(
							2
						)} als Verlust gebucht`,
						type: "success",
					});
				}
			} finally {
				setLossBookingLoading(false);
				setLossBookingDialogOpen(false);
				setSelectedInvoiceForLoss(null);
			}
		},
		[createPayment, selectedInvoiceForLoss, uiCallbacks]
	);

	// ✅ CANCEL INVOICE - All logic inside AbstractInvoiceTable
	const handleCancelInvoice = useCallback(
		(invoiceId: number) => {
			if (!cancelInvoice) return;

			const invoice = tableData.find(
				(inv) => (inv as any).invoice_id === invoiceId
			) as TableInvoiceRow | undefined;

			// Check if invoice has Type 3 allocations (akonto payments) that must be canceled first
			if (invoice?.subRows) {
				const hasType3Allocations = invoice.subRows.some(
					(payment) =>
						payment.allocation_type === 3 &&
						!payment.allocation_canceled_at
				);

				if (hasType3Allocations) {
					showNotification({
						message:
							"Diese Rechnung hat Akontozahlungen, die zuerst manuell storniert werden müssen.",
						type: "error",
					});
					return;
				}
			}

			// Open dialog if callback provided
			if (uiCallbacks?.onCancellationDialogOpen && invoice) {
				uiCallbacks.onCancellationDialogOpen(invoice);
			}
		},
		[tableData, cancelInvoice, uiCallbacks]
	);

	// ✅ OPEN PAYMENT DIALOG - All logic inside AbstractInvoiceTable
	const handleOpenPaymentDialog = useCallback(
		(invoice: TableInvoiceRow) => {
			if (features?.historicalView) {
				showNotification({
					message:
						"Zahlungen können nur für aktuelle Daten erfasst werden",
					type: "warning",
				});
				return;
			}

			if (uiCallbacks?.onPaymentDialogOpen) {
				uiCallbacks.onPaymentDialogOpen(invoice);
			}
		},
		[uiCallbacks, features]
	);

	// ✅ SEND TO ZAK - Placeholder
	const handleSendToZak = useCallback((invoiceId: number) => {
		showNotification({
			message: "ZAK-Funktionalität noch nicht implementiert",
			type: "info",
		});
	}, []);

	const handleShowJobDocuments = useCallback((invoice: TableInvoiceRow) => {
		setSelectedInvoiceForJobDocuments(invoice);
		setJobDocumentsDialogOpen(true);
	}, []);

	// Handler for payment actions button click
	const handlePaymentActionsClick = useCallback(
		(payment: TablePaymentRow, event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();

			setContextMenu({
				visible: true,
				x: event.clientX,
				y: event.clientY,
				row: payment,
			});
		},
		[]
	);

	// Handler for payment-journal sub-row actions button click
	const handlePaymentJournalSubRowActionsClick = useCallback(
		(allocation: InvoiceAllocationSubRow, event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();

			setContextMenu({
				visible: true,
				x: event.clientX,
				y: event.clientY,
				row: allocation as unknown as TableRowData,
			});
		},
		[]
	);

	const handleCancelPayment = useCallback(
		async (allocationId: number, invoiceId: number, note: string) => {
			if (!cancelPayment) return;

			if (features?.historicalView || features?.readOnlyMode) {
				showNotification({
					message:
						"Zahlungen können nur für aktuelle Daten storniert werden",
					type: "warning",
				});
				return;
			}

			await cancelPayment(allocationId, invoiceId, note);

			if (uiCallbacks?.onRefreshData) {
				await uiCallbacks.onRefreshData();
			}
		},
		[cancelPayment, features, uiCallbacks]
	);

	const handleCancelLossAllocation = useCallback(
		async (allocationId: number, invoiceId: number, note: string) => {
			if (!cancelLossAllocation) return;

			if (features?.historicalView || features?.readOnlyMode) {
				showNotification({
					message:
						"Verluste können nur für aktuelle Daten storniert werden",
					type: "warning",
				});
				return;
			}

			await cancelLossAllocation(allocationId, invoiceId, note);

			if (uiCallbacks?.onRefreshData) {
				await uiCallbacks.onRefreshData();
			}
		},
		[cancelLossAllocation, features, uiCallbacks]
	);

	const handleRebookPaymentAsAkonto = useCallback(
		async (allocationId: number, invoiceId: number, note: string) => {
			if (!rebookPaymentAsAkonto) return;

			if (features?.historicalView || features?.readOnlyMode) {
				showNotification({
					message:
						"Zahlungen können nur für aktuelle Daten umgebucht werden",
					type: "warning",
				});
				return;
			}

			await rebookPaymentAsAkonto(allocationId, invoiceId, note);

			if (uiCallbacks?.onRefreshData) {
				await uiCallbacks.onRefreshData();
			}
		},
		[rebookPaymentAsAkonto, features, uiCallbacks]
	);

	// ✅ DYNAMIC COLUMN BUILDING - Based on tableType and features
	const columns = useMemo<ColumnDef<TableRowData>[]>(() => {
		const cols: ColumnDef<TableRowData>[] = [];

		// Always include these base columns
		cols.push(
			createExpanderColumn<TableRowData>({
				showVATFooter: showVATSummary,
				showPaymentFooter: showPaymentSummary,
			})
		);
		cols.push(
			createInvoiceNumberColumn<TableRowData>({
				showVATFooter: showVATSummary,
				showPaymentFooter: showPaymentSummary,
			})
		);

		// Add columns based on table type
		switch (tableType) {
			case "canceled":
				cols.push(createPaymentStatusColumn<TableRowData>());
				cols.push(createRecipientColumn<TableRowData>());
				cols.push(createClientColumn<TableRowData>());
				cols.push(createInvoiceAmountColumn<TableRowData>());
				cols.push(createCurrencyColumn<TableRowData>());
				cols.push(createInvoiceDateColumn<TableRowData>());
				cols.push(createCancellationDateColumn<TableRowData>());
				cols.push(createCancellationReasonColumn<TableRowData>());
				break;

			case "payment-journal":
				cols.length = 0; // Clear default columns for payment-centric view
				cols.push(
					createPaymentExpanderColumn() as unknown as ColumnDef<TableRowData>
				);
				cols.push(
					createPaymentJournalValutaDateColumn() as unknown as ColumnDef<TableRowData>
				);
				cols.push(
					createPaymentJournalValueColumn() as unknown as ColumnDef<TableRowData>
				);
				cols.push(
					createPaymentJournalTypeColumn() as unknown as ColumnDef<TableRowData>
				);
				cols.push(
					createPaymentJournalSenderColumn() as unknown as ColumnDef<TableRowData>
				);
				cols.push(
					createPaymentJournalNoteColumn() as unknown as ColumnDef<TableRowData>
				);
				break;

			default: // 'active' or 'open-items'
				cols.push(
					createPaymentPercentageColumn<TableRowData>({
						showPaymentFooter: showPaymentSummary,
					})
				);
				cols.push(
					createRecipientColumn<TableRowData>({
						showPaymentFooter: showPaymentSummary,
					})
				);
				cols.push(createClientColumn<TableRowData>());
				cols.push(
					createVATRateColumn<TableRowData>({
						showVATFooter: showVATSummary,
						showPaymentFooter: showPaymentSummary,
					})
				);
				cols.push(
					createVATNetAmountColumn<TableRowData>({
						showVATFooter: showVATSummary,
						showPaymentFooter: showPaymentSummary,
					})
				);
				cols.push(
					createVATAmountColumn<TableRowData>({
						showVATFooter: showVATSummary,
						showPaymentFooter: showPaymentSummary,
					})
				);
				cols.push(
					createVATGrossAmountColumn<TableRowData>({
						showVATFooter: showVATSummary,
						showPaymentFooter: showPaymentSummary,
					})
				);
				cols.push(
					createOpenAmountColumn<TableRowData>({
						showPaymentFooter: showPaymentSummary,
					})
				);
				cols.push(createCurrencyColumn<TableRowData>());
				cols.push(createInvoiceDateColumn<TableRowData>());
		}

		// Sharing column if enabled
		if (features?.allowSharing) {
			cols.push(
				createSharingColumn<TableRowData>((invoice: TableRowData) => {
					if (!isPaymentRow(invoice)) {
						handleShareToggle(invoice as any);
					}
				})
			);
		}

		if (features?.allowZak) {
			// ZAK status column
			cols.push(createZakStatusColumn<TableRowData>());
		}

		// Actions column with INTERNAL handlers
		cols.push(
			createActionsColumn<TableRowData>({
				onOpenFile: handleOpenFile,
				onOpenFileInNewTab: handleOpenFileInNewTab,
				onPrintAsCopy: handlePrintAsCopy,
				onOpenPaymentDialog: features?.allowPayments
					? (invoice: TableInvoiceRow) =>
							handleOpenPaymentDialog(invoice)
					: undefined,
				onBookRemainderAsLoss: features?.allowBookAsLoss
					? (invoice: TableInvoiceRow) =>
							handleBookRemainderAsLoss(invoice)
					: undefined,
				onShareToggle: features?.allowSharing
					? (invoice: TableInvoiceRow) => handleShareToggle(invoice)
					: undefined,
				onCancelInvoice: features?.allowCancelInvoice
					? handleCancelInvoice
					: undefined,
				onSendToZak: features?.allowZak ? handleSendToZak : undefined,
				onShowJobDocuments: handleShowJobDocuments,
				reminderActions: uiCallbacks?.reminderActions,
				onPaymentActionsClick: handlePaymentActionsClick,
				isZakEnabled: features?.allowZak,
				features: features,
			})
		);

		return cols;
	}, [
		tableType,
		features,
		showVATSummary,
		handleShareToggle,
		handleOpenPaymentDialog,
		handleBookRemainderAsLoss,
		handleCancelInvoice,
		handleSendToZak,
		handleShowJobDocuments,
		handleOpenFile,
		handleOpenFileInNewTab,
		handlePrintAsCopy,
		handlePaymentActionsClick,
		handlePaymentJournalSubRowActionsClick,
		uiCallbacks?.reminderActions,
	]);

	// Payment row columns (independent from invoice columns)
	const paymentRowColumns = useMemo<PaymentColumnDef[]>(
		() => buildPaymentRowColumns(),
		[]
	);

	// Invoice allocation sub-row columns (for payment-journal view)
	const invoiceAllocationColumns = useMemo<InvoiceAllocationColumnDef[]>(
		() => buildInvoiceAllocationSubRowColumns(),
		[]
	);

	const contextMenuActions = useMemo(
		() => ({
			onOpenFile: handleOpenFile,
			onOpenFileInNewTab: handleOpenFileInNewTab,
			onPrintAsCopy: handlePrintAsCopy,
			onOpenPaymentDialog: features?.allowPayments
				? (invoice: TableInvoiceRow) => handleOpenPaymentDialog(invoice)
				: undefined,
			onBookRemainderAsLoss: features?.allowBookAsLoss
				? (invoice: TableInvoiceRow) =>
						handleBookRemainderAsLoss(invoice)
				: undefined,
			onShareToggle: features?.allowSharing
				? (invoice: TableInvoiceRow) => handleShareToggle(invoice)
				: undefined,
			onCancelInvoice: features?.allowCancelInvoice
				? handleCancelInvoice
				: undefined,
			onSendToZak: features?.allowZak ? handleSendToZak : undefined,
			onShowJobDocuments: handleShowJobDocuments,
			reminderActions: uiCallbacks?.reminderActions,
			onCancelPayment: handleCancelPayment,
			onCancelLossAllocation: handleCancelLossAllocation,
			onRebookPaymentAsAkonto: handleRebookPaymentAsAkonto,
			paymentActionsReadOnly: features?.readOnlyMode,
		}),
		[
			features,
			handleOpenPaymentDialog,
			handleBookRemainderAsLoss,
			handleShareToggle,
			handleCancelInvoice,
			handleSendToZak,
			handleShowJobDocuments,
			handleOpenFile,
			handleOpenFileInNewTab,
			handlePrintAsCopy,
			handleCancelPayment,
			handleCancelLossAllocation,
			handleRebookPaymentAsAkonto,
			uiCallbacks?.reminderActions,
		]
	);

	// Row color function based on table type
	const getRowColor = useCallback(
		(row: TableRowData, highlightedRows?: number[]) => {
			// Payment-centric view (payment-journal): invoice sub-rows get subtle color
			if (
				tableType === "payment-journal" &&
				isInvoiceSubRow(row as PaymentJournalTableRowData)
			) {
				return "bg-gray-50/50";
			}

			if (isPaymentRow(row)) {
				const paymentRow = row as TablePaymentRow;

				// Check if payment is canceled or rebooked
				const isPaymentCanceled = !!paymentRow.payment_canceled_at;
				const isAllocationCanceled =
					!!paymentRow.allocation_canceled_at;

				const isLossAllocation = paymentRow.payment_id === null;

				if (isLossAllocation && isAllocationCanceled) {
					return "bg-gray-50"; // Canceled loss - grayed out
				}

				if (isPaymentCanceled && isAllocationCanceled) {
					return "bg-gray-50"; // Storniert - subtle gray background
				}

				if (isAllocationCanceled && !isPaymentCanceled) {
					return "bg-orange-50/30"; // Als Akonto umgebucht - subtle orange background
				}

				if (isLossAllocation) {
					return "bg-red-50/30";
				}
				// Different colors based on table type - all subtle
				switch (tableType) {
					case "canceled":
						return "bg-gray-50/50"; // Historical payment rows
					case "payment-journal":
						return "bg-green-50/30"; // Payment rows in payment journal
					default:
						return "bg-blue-50/30"; // Active payment rows
				}
			}

			if (
				isInvoiceRow(row) &&
				highlightedRows?.includes((row as any).invoice_id || 0)
			) {
				return "bg-yellow-100 border-yellow-200";
			}

			return "";
		},
		[tableType]
	);

	// Handler for page size changes
	const handlePageSizeChange = (newPageSize: string) => {
		if (onPaginationChange && pagination) {
			onPaginationChange({
				pageIndex: 0, // Reset to first page
				pageSize: Number(newPageSize),
			});
		}
	};

	// Close context menu on outside click
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (contextMenu.visible) {
				setContextMenu({ visible: false, x: 0, y: 0, row: null });
			}
		};

		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, [contextMenu.visible]);

	const table = useReactTable({
		data: tableData,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSubRows: (row: TableRowData) => {
			// For payment-journal: primary payment rows have invoice allocation subRows
			// For other tables: invoice rows have payment subRows
			if ("isInvoiceSubRow" in row) {
				// Payment journal: sub-rows are invoice allocations
				return row.isInvoiceSubRow ? undefined : row.subRows;
			}
			// Other tables: sub-rows are payments
			return isPaymentRow(row)
				? undefined
				: (row.subRows as TablePaymentRow[]);
		},
		columnResizeMode: "onChange",
		// Manual control for server-side operations
		manualSorting,
		manualPagination,
		manualFiltering,
		// Row count for pagination
		rowCount: totalRowCount || tableData.length,
		// State management
		state: {
			columnSizing,
			...(sorting && { sorting }),
			...(pagination && { pagination }),
			...(globalFilter !== undefined && { globalFilter }),
		},
		// State change handlers
		onColumnSizingChange: setColumnSizing,
		...(onSortingChange && {
			onSortingChange: (updaterOrValue) => {
				const newSorting =
					typeof updaterOrValue === "function"
						? updaterOrValue(sorting || [])
						: updaterOrValue;
				onSortingChange(newSorting);
			},
		}),
		...(onPaginationChange && {
			onPaginationChange: (updaterOrValue) => {
				const newPagination =
					typeof updaterOrValue === "function"
						? updaterOrValue(
								pagination || { pageIndex: 0, pageSize: 10 }
							)
						: updaterOrValue;
				onPaginationChange(newPagination);
			},
		}),
		...(onGlobalFilterChange && {
			onGlobalFilterChange: (updaterOrValue) => {
				const newFilter =
					typeof updaterOrValue === "function"
						? updaterOrValue(globalFilter || "")
						: updaterOrValue;
				onGlobalFilterChange(newFilter);
			},
		}),
		// Table features
		enableColumnResizing: true,
		enableSorting: true,
		enableFilters: true,
		enableGlobalFilter: true,
		enableExpanding: true,
	});

	// Handler for right-click context menu
	const handleContextMenu = (event: React.MouseEvent, row: TableRowData) => {
		if (!contextMenuActions) return;

		event.preventDefault();
		event.stopPropagation();

		// Use absolute viewport coordinates for fixed positioning
		setContextMenu({
			visible: true,
			x: event.clientX,
			y: event.clientY,
			row,
		});
	};

	// Handler for row click (expansion if enabled)
	const handleRowClick = (row: Row<TableRowData>) => {
		if (isInvoiceRow(row.original) && row.getCanExpand()) {
			row.toggleExpanded();
		}
	};

	// Render payment row cell using independent payment columns
	const renderPaymentRowCell = (
		columnDef: PaymentColumnDef,
		payment: TablePaymentRow
	): React.ReactNode => {
		return columnDef.render(payment, handlePaymentActionsClick);
	};

	// Loading state
	if (loading) {
		return (
			<div className="inline-block w-full">
				<div className="overflow-auto relative flex items-center justify-center gap-3 py-10">
					<Loader2 className="h-6 w-6 animate-spin text-blue-600" />
					<span className="text-sm text-gray-600">
						Ihre Daten werden geladen...
					</span>
				</div>
			</div>
		);
	}

	// Empty state
	if (data.length === 0) {
		return <EmptyDataPlaceholder label={emptyStateLabel} />;
	}

	return (
		<div className="w-full flex flex-col items-center">
			<div className="overflow-x-auto relative" ref={tableRef}>
				<Table className="table-auto w-auto mx-auto">
					<TableHeader className="sticky top-0 bg-white z-20">
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									// Determine alignment based on column type
									const columnId = header.column.id;
									const isNumberColumn = [
										"payment_percentage",
										"vat_rate",
										"net_amount",
										"vat_amount",
										"gross_amount",
										"open_amount",
										"paid_amount",
										"invoice_value",
									].includes(columnId);
									const alignment = isNumberColumn
										? "text-right"
										: "text-left";

									return (
										<TableHead
											key={header.id}
											className={cn(
												"px-2 py-3",
												alignment
											)}
										>
											<div className="space-y-2">
												<div
													className={cn(
														"flex items-center gap-2",
														isNumberColumn
															? "justify-end"
															: "justify-start",
														header.column.getCanSort()
															? "cursor-pointer select-none"
															: ""
													)}
													onClick={header.column.getToggleSortingHandler()}
												>
													{header.isPlaceholder
														? null
														: flexRender(
																header.column
																	.columnDef
																	.header,
																header.getContext()
															)}
													{header.column.getCanSort() && (
														<div className="flex flex-col">
															{header.column.getIsSorted() ===
																"asc" && (
																<ChevronUp className="h-3 w-3" />
															)}
															{header.column.getIsSorted() ===
																"desc" && (
																<ChevronDown className="h-3 w-3" />
															)}
															{!header.column.getIsSorted() && (
																<div className="h-3 w-3 opacity-50">
																	<ChevronUp className="h-2 w-2" />
																	<ChevronDown className="h-2 w-2 -mt-1" />
																</div>
															)}
														</div>
													)}
												</div>
											</div>
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows.map((row, index) => {
							const isCanceledPayment =
								isPaymentRow(row.original) &&
								(row.original.allocation_canceled_at ||
									row.original.payment_canceled_at);

							// Check if this row is an invoice allocation sub-row (payment-journal)
							const isAllocationSubRow =
								tableType === "payment-journal" &&
								isInvoiceSubRow(
									row.original as PaymentJournalTableRowData
								);

							// Check if this is the first allocation subrow (for payment-journal)
							const isFirstAllocationSubrow =
								isAllocationSubRow &&
								(index === 0 ||
									!isInvoiceSubRow(
										table.getRowModel().rows[index - 1]
											?.original as PaymentJournalTableRowData
									));

							// Check if this is the first payment subrow (right after an expanded invoice)
							const isFirstPaymentSubrow =
								isPaymentRow(row.original) &&
								(index === 0 ||
									!isPaymentRow(
										table.getRowModel().rows[index - 1]
											?.original
									));

							// Get parent invoice row for column structure
							const parentRow =
								isPaymentRow(row.original) && index > 0
									? table
											.getRowModel()
											.rows.slice(0, index)
											.reverse()
											.find((r) =>
												isInvoiceRow(r.original)
											)
									: null;

							// Get parent payment row for allocation sub-rows
							const parentPaymentRow =
								isAllocationSubRow && index > 0
									? table
											.getRowModel()
											.rows.slice(0, index)
											.reverse()
											.find((r) =>
												isPaymentPrimaryRow(
													r.original as PaymentJournalTableRowData
												)
											)
									: null;

							// Check if any payment in this expanded group is canceled
							const hasCanceledPayments =
								parentRow && isInvoiceRow(parentRow.original)
									? (parentRow.original.subRows || []).some(
											(subRow: any) =>
												subRow.payment_canceled_at ||
												subRow.allocation_canceled_at
										)
									: false;

							return (
								<Fragment key={row.id}>
									{/* Invoice allocation sub-row header (payment-journal) */}
									{isFirstAllocationSubrow &&
										parentPaymentRow && (
											<TableRow
												key={`${row.id}-allocation-header`}
												className="bg-gray-50 border-t border-gray-200 hover:bg-gray-50 sticky top-[49px] z-[9]"
											>
												<TableCell
													className="px-2 py-1"
													style={{ width: "40px" }}
												></TableCell>
												<TableCell
													colSpan={
														table.getAllColumns()
															.length - 1
													}
													className="px-2 py-1"
												>
													<div className="flex gap-4 items-center">
														{invoiceAllocationColumns.map(
															(column) => (
																<div
																	key={`allocation-header-${column.id}`}
																	className="text-xs font-semibold text-gray-600"
																	style={{
																		width: column.width,
																		minWidth:
																			column.minWidth,
																	}}
																>
																	{
																		column.header
																	}
																</div>
															)
														)}
													</div>
												</TableCell>
											</TableRow>
										)}
									{/* Payment sub-row header (other tables) */}
									{isFirstPaymentSubrow && parentRow && (
										<TableRow
											key={`${row.id}-header`}
											className="bg-gray-50 border-t border-gray-200 hover:bg-gray-50 sticky top-[49px] z-[9]"
										>
											<TableCell
												className="px-2 py-1"
												style={{ width: "40px" }}
											></TableCell>
											<TableCell
												colSpan={
													table.getAllColumns()
														.length - 1
												}
												className="px-2 py-1"
											>
												<div className="flex gap-4">
													{paymentRowColumns.map(
														(paymentColumn) => {
															// Hide "Stornierungsgrund" header if no canceled payments
															if (
																paymentColumn?.id ===
																	"payment-cancellation-reason" &&
																!hasCanceledPayments
															) {
																return (
																	<div
																		key={`header-${paymentColumn.id}`}
																		className="text-xs font-semibold text-gray-600"
																		style={{
																			width: paymentColumn.width,
																			minWidth:
																				paymentColumn.minWidth,
																		}}
																	></div>
																);
															}

															const isFlexColumn =
																paymentColumn.id ===
																	"payment-note" ||
																paymentColumn.id ===
																	"payment-cancellation-reason";
															return (
																<div
																	key={`header-${paymentColumn.id}`}
																	className={`text-xs font-semibold text-gray-600 ${
																		isFlexColumn
																			? "flex-1"
																			: ""
																	}`}
																	style={{
																		width: isFlexColumn
																			? undefined
																			: paymentColumn.width,
																		minWidth:
																			paymentColumn.minWidth,
																	}}
																>
																	{paymentColumn?.header ||
																		""}
																</div>
															);
														}
													)}
												</div>
											</TableCell>
										</TableRow>
									)}
									<TableRow
										key={`${row.id}-data`}
										className={cn(
											"group h-8 transition-colors",
											isPaymentRow(row.original) &&
												"hover:bg-gray-100",
											isAllocationSubRow &&
												"hover:bg-gray-100",
											getRowColor?.(
												row.original,
												highlightedRows
											),
											isPaymentRow(row.original) &&
												"border-t-0",
											isAllocationSubRow && "border-t-0",
											isInvoiceRow(row.original) &&
												row.subRows &&
												row.subRows.length > 0 &&
												"cursor-pointer",
											isCanceledPayment && "opacity-50"
										)}
										onClick={() => handleRowClick(row)}
										onContextMenu={
											tableType !== "payment-journal"
												? (e) =>
														handleContextMenu(
															e,
															row.original
														)
												: undefined
										}
									>
										{/* Invoice allocation sub-row content (payment-journal) */}
										{isAllocationSubRow ? (
											<>
												<TableCell
													className="px-2 py-1"
													style={{ width: "40px" }}
												></TableCell>
												<TableCell
													colSpan={
														table.getAllColumns()
															.length - 1
													}
													className="px-2 py-1"
												>
													<div className="flex gap-4 items-center">
														{invoiceAllocationColumns.map(
															(column) => (
																<div
																	key={`allocation-${row.id}-${column.id}`}
																	className="text-sm"
																	style={{
																		width: column.width,
																		minWidth:
																			column.minWidth,
																	}}
																>
																	{column.render(
																		row.original as unknown as InvoiceAllocationSubRow
																	)}
																</div>
															)
														)}
													</div>
												</TableCell>
											</>
										) : isPaymentRow(row.original) &&
										  parentRow ? (
											<>
												<TableCell
													className="px-2 py-1"
													style={{ width: "40px" }}
												></TableCell>
												<TableCell
													colSpan={
														table.getAllColumns()
															.length - 1
													}
													className="px-2 py-1"
												>
													<div className="flex gap-4">
														{paymentRowColumns.map(
															(paymentColumn) => {
																const isFlexColumn =
																	paymentColumn.id ===
																		"payment-note" ||
																	paymentColumn.id ===
																		"payment-cancellation-reason";
																return (
																	<div
																		key={`payment-${row.id}-${paymentColumn.id}`}
																		className={`text-sm ${
																			isFlexColumn
																				? "flex-1"
																				: ""
																		}`}
																		style={{
																			width: isFlexColumn
																				? undefined
																				: paymentColumn.width,
																			minWidth:
																				paymentColumn.minWidth,
																			maxWidth:
																				isFlexColumn
																					? undefined
																					: paymentColumn.maxWidth,
																		}}
																	>
																		{renderPaymentRowCell(
																			paymentColumn,
																			row.original as TablePaymentRow
																		)}
																	</div>
																);
															}
														)}
													</div>
												</TableCell>
											</>
										) : (
											<>
												{/* Invoice row: use standard TanStack columns */}
												{row
													.getVisibleCells()
													.map((cell) => {
														return (
															<TableCell
																key={cell.id}
																className="px-2 py-1 text-sm"
																style={{
																	width: cell.column.getSize(),
																	minWidth:
																		cell
																			.column
																			.columnDef
																			.minSize,
																	maxWidth:
																		cell
																			.column
																			.columnDef
																			.maxSize,
																}}
															>
																{flexRender(
																	cell.column
																		.columnDef
																		.cell,
																	cell.getContext()
																)}
															</TableCell>
														);
													})}
											</>
										)}
									</TableRow>
								</Fragment>
							);
						})}
					</TableBody>
				</Table>

				{unifiedSummary && unifiedSummary.groups.length > 0 && (
					<div className="mt-6 flex justify-center px-2">
						<div className="w-full" style={summaryContainerStyle}>
							<div className="border-t-2 border-slate-200 mb-4" />
							<div className="overflow-x-auto">
								<div className="flex justify-center">
									<table className="text-base table-auto">
										<thead className="text-sm font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
											<tr>
												<th
													className="px-5 py-3 text-center text-black text-xl"
													colSpan={6}
												>
													{summaryTitle}
												</th>
											</tr>
											<tr className="text-slate-600 text-base">
												<th className="px-5 py-2.5 text-left">
													Währung
												</th>
												<th className="px-5 py-2.5 text-left">
													MwSt-Satz
												</th>
												<th className="px-5 py-2.5 text-right">
													{unifiedSummary.countLabel}
												</th>
												<th className="px-5 py-2.5 text-right">
													{netLabel}
												</th>
												<th className="px-5 py-2.5 text-right">
													{vatLabel}
												</th>
												<th className="px-5 py-2.5 text-right">
													{grossLabel}
												</th>
											</tr>
										</thead>
										<tbody>
											{unifiedSummary.groups.map(
												(group, groupIndex) => {
													const hasSingleEntry =
														group.entries.length ===
														1;

													if (
														hasSingleEntry &&
														group.entries[0]
													) {
														const entry =
															group.entries[0];
														const boldCellClass =
															"font-semibold text-slate-900 text-base";
														return (
															<tr
																key={`summary-${entry.currency}-single`}
																className={cn(
																	"bg-slate-50",
																	groupIndex >
																		0
																		? "border-t-2 border-slate-300"
																		: ""
																)}
															>
																<td
																	className={cn(
																		"px-5 py-2.5 text-left",
																		boldCellClass
																	)}
																>
																	{
																		entry.currency
																	}
																</td>
																<td
																	className={cn(
																		"px-5 py-2.5 text-left",
																		boldCellClass
																	)}
																>
																	{entry.isAkonto
																		? "Akontoguthaben"
																		: formatPercentage(
																				entry.taxRate
																			)}
																</td>
																<td
																	className={cn(
																		"px-5 py-2.5 text-right",
																		boldCellClass
																	)}
																>
																	{
																		entry.count
																	}
																</td>
																<td
																	className={cn(
																		"px-5 py-2.5 text-right font-mono",
																		boldCellClass
																	)}
																>
																	{formatCurrency(
																		entry.netAmount
																	)}
																</td>
																<td
																	className={cn(
																		"px-5 py-2.5 text-right font-mono",
																		boldCellClass
																	)}
																>
																	{entry.isAkonto
																		? "–"
																		: formatCurrency(
																				entry.vatAmount
																			)}
																</td>
																<td
																	className={cn(
																		"px-5 py-2.5 text-right font-mono text-green-600",
																		boldCellClass
																	)}
																>
																	{formatCurrency(
																		entry.grossAmount
																	)}
																</td>
															</tr>
														);
													}

													return (
														<Fragment
															key={`summary-group-${group.currency}`}
														>
															{group.entries.map(
																(
																	entry,
																	entryIndex
																) => (
																	<tr
																		key={`summary-${entry.currency}-${entry.taxRate}-${entryIndex}`}
																		className={cn(
																			entryIndex ===
																				0 &&
																				groupIndex >
																					0
																				? "border-t-2 border-slate-300"
																				: ""
																		)}
																	>
																		<td className="px-5 py-2.5 text-left font-medium text-slate-700 text-base">
																			{
																				entry.currency
																			}
																		</td>
																		<td className="px-5 py-2.5 text-left font-medium text-slate-700 text-base">
																			{entry.isAkonto
																				? "Akontoguthaben"
																				: formatPercentage(
																						entry.taxRate
																					)}
																		</td>
																		<td className="px-5 py-2.5 text-right text-slate-600 text-base">
																			{
																				entry.count
																			}
																		</td>
																		<td className="px-5 py-2.5 text-right font-mono text-base">
																			{formatCurrency(
																				entry.netAmount
																			)}
																		</td>
																		<td className="px-5 py-2.5 text-right font-mono text-base">
																			{entry.isAkonto
																				? "–"
																				: formatCurrency(
																						entry.vatAmount
																					)}
																		</td>
																		<td className="px-5 py-2.5 text-right font-mono text-base">
																			{formatCurrency(
																				entry.grossAmount
																			)}
																		</td>
																	</tr>
																)
															)}
															{group.entries
																.length > 1 && (
																<tr className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-700 text-base">
																	<td
																		className="px-5 py-2.5 text-left"
																		colSpan={
																			2
																		}
																	>
																		Gesamt -{" "}
																		{
																			group.currency
																		}
																	</td>
																	<td className="px-5 py-2.5 text-right">
																		{
																			group
																				.total
																				.count
																		}
																	</td>
																	<td className="px-5 py-2.5 text-right font-mono">
																		{formatCurrency(
																			group
																				.total
																				.netAmount
																		)}
																	</td>
																	<td className="px-5 py-2.5 text-right font-mono">
																		{formatCurrency(
																			group
																				.total
																				.vatAmount
																		)}
																	</td>
																	<td className="px-5 py-2.5 text-right font-mono">
																		{formatCurrency(
																			group
																				.total
																				.grossAmount
																		)}
																	</td>
																</tr>
															)}
														</Fragment>
													);
												}
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Context Menu */}
				{contextMenu.visible &&
					contextMenuActions &&
					(() => {
						const row = contextMenu.row;

						// Handle payment-journal sub-rows (invoice allocations)
						if (
							tableType === "payment-journal" &&
							row &&
							isInvoiceSubRow(row as PaymentJournalTableRowData)
						) {
							const allocation = row as InvoiceAllocationSubRow;
							// Convert InvoiceAllocationSubRow to TablePaymentRow format for PaymentContextMenu
							const paymentLikeRow: TablePaymentRow = {
								allocation_id: allocation.allocation_id,
								invoice_id: allocation.invoice_id,
								payment_id: allocation.payment_id,
								allocated_amount: allocation.allocated_amount,
								allocation_note: allocation.allocation_note,
								allocation_created_at:
									allocation.allocation_created_at,
								allocation_canceled_at: (allocation as any)
									.allocation_canceled_at, //TODO Should handle type correctly, I have no time left it is 2:25am
								allocation_type: 1,
								payment_type: "",
								payment_value: null,
								payment_currency: allocation.invoice_currency,
								payment_valuta_date: null,
								payment_entry_date: null,
								payment_created_at: null,
								payment_note_1: undefined,
								payment_note_2: undefined,
								payment_canceled_at: null,
								is_loss: false,
								isPaymentRow: true,
							};
							return (
								<PaymentContextMenu
									x={contextMenu.x}
									y={contextMenu.y}
									visible={contextMenu.visible}
									payment={paymentLikeRow}
									onCancelPayment={(
										allocationId,
										invoiceId,
										note
									) =>
										contextMenuActions.onCancelPayment?.(
											allocationId,
											invoiceId,
											note
										)
									}
									onCancelLossAllocation={(
										allocationId,
										invoiceId,
										note
									) =>
										contextMenuActions.onCancelLossAllocation?.(
											allocationId,
											invoiceId,
											note
										)
									}
									onRebookPaymentAsAkonto={(
										allocationId,
										invoiceId,
										note
									) =>
										contextMenuActions.onRebookPaymentAsAkonto?.(
											allocationId,
											invoiceId,
											note
										)
									}
									onClose={() =>
										setContextMenu({
											visible: false,
											x: 0,
											y: 0,
											row: null,
										})
									}
									readOnly={
										contextMenuActions.paymentActionsReadOnly
									}
								/>
							);
						}

						if (isPaymentRow(row)) {
							return (
								<PaymentContextMenu
									x={contextMenu.x}
									y={contextMenu.y}
									visible={contextMenu.visible}
									payment={row}
									onCancelPayment={(
										allocationId,
										invoiceId,
										note
									) =>
										contextMenuActions.onCancelPayment?.(
											allocationId,
											invoiceId,
											note
										)
									}
									onCancelLossAllocation={(
										allocationId,
										invoiceId,
										note
									) =>
										contextMenuActions.onCancelLossAllocation?.(
											allocationId,
											invoiceId,
											note
										)
									}
									onRebookPaymentAsAkonto={(
										allocationId,
										invoiceId,
										note
									) =>
										contextMenuActions.onRebookPaymentAsAkonto?.(
											allocationId,
											invoiceId,
											note
										)
									}
									onClose={() =>
										setContextMenu({
											visible: false,
											x: 0,
											y: 0,
											row: null,
										})
									}
									readOnly={
										contextMenuActions.paymentActionsReadOnly
									}
								/>
							);
						} else if (isInvoiceRow(row)) {
							// Type guard ensures row is InvoicePaymentsViewType-like
							const invoiceData =
								row as unknown as TableInvoiceRow;
							return (
								<InvoiceContextMenu
									x={contextMenu.x}
									y={contextMenu.y}
									visible={contextMenu.visible}
									invoice={invoiceData}
									onOpenFile={contextMenuActions.onOpenFile}
									onOpenFileInNewTab={
										contextMenuActions.onOpenFileInNewTab
									}
									onPrintAsCopy={
										contextMenuActions.onPrintAsCopy
									}
									onOpenPaymentDialog={
										contextMenuActions.onOpenPaymentDialog
									}
									onBookRemainderAsLoss={
										contextMenuActions.onBookRemainderAsLoss
									}
									onShareToggle={
										contextMenuActions.onShareToggle
									}
									onCancelInvoice={
										contextMenuActions.onCancelInvoice
									}
									onSendToZak={contextMenuActions.onSendToZak}
									onShowJobDocuments={
										contextMenuActions.onShowJobDocuments
									}
									reminderActions={
										contextMenuActions.reminderActions
									}
									onClose={() =>
										setContextMenu({
											visible: false,
											x: 0,
											y: 0,
											row: null,
										})
									}
									isZakEnabled={features?.allowZak || false}
									features={features}
								/>
							);
						}
						return null;
					})()}
			</div>

			{/* Pagination */}
			{pagination && onPaginationChange && (
				<div className="flex items-center justify-between px-2 py-4 border-t">
					<div className="flex items-center space-x-4">
						{/* Page Size Selector */}
						<div className="flex items-center space-x-2">
							<span className="text-sm text-gray-700">
								Zeilen pro Seite:
							</span>
							<Select
								value={pagination.pageSize.toString()}
								onValueChange={handlePageSizeChange}
							>
								<SelectTrigger className="w-20">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{PAGE_SIZE_OPTIONS.map((size) => (
										<SelectItem
											key={size}
											value={size.toString()}
										>
											{size}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<p className="text-sm text-gray-700">
							Seite {pagination.pageIndex + 1} von{" "}
							{Math.ceil(
								(totalRowCount || data.length) /
									pagination.pageSize
							)}
						</p>
						<p className="text-sm text-gray-500">
							{(() => {
								const total = totalRowCount || data.length;
								const startEntry =
									pagination.pageIndex * pagination.pageSize +
									1;
								const endEntry = Math.min(
									(pagination.pageIndex + 1) *
										pagination.pageSize,
									total
								);
								return `${startEntry}-${endEntry} von ${total} Einträgen`;
							})()}
						</p>
					</div>
					<div className="flex items-center space-x-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								onPaginationChange({
									...pagination,
									pageIndex: 0,
								})
							}
							disabled={!table.getCanPreviousPage()}
						>
							Erste
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								onPaginationChange({
									...pagination,
									pageIndex: pagination.pageIndex - 1,
								})
							}
							disabled={!table.getCanPreviousPage()}
						>
							Zurück
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								onPaginationChange({
									...pagination,
									pageIndex: pagination.pageIndex + 1,
								})
							}
							disabled={!table.getCanNextPage()}
						>
							Weiter
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								onPaginationChange({
									...pagination,
									pageIndex: table.getPageCount() - 1,
								})
							}
							disabled={!table.getCanNextPage()}
						>
							Letzte
						</Button>
					</div>
				</div>
			)}

			{/* FileViewer Modal */}
			<FileViewerModal
				fileIds={allFileIds}
				defaultFileId={selectedFileId || undefined}
				isOpen={isViewerOpen}
				onClose={() => {
					setIsViewerOpen(false);
					setSelectedFileId(null);
				}}
			/>

			{/* Book Remainder as Loss Dialog */}
			<BookRemainderAsLossDialog
				isOpen={lossBookingDialogOpen}
				onClose={() => {
					setLossBookingDialogOpen(false);
					setSelectedInvoiceForLoss(null);
				}}
				onConfirm={handleConfirmLossBooking}
				invoice={selectedInvoiceForLoss}
				remainingAmount={(() => {
					if (!selectedInvoiceForLoss) return 0;
					const payments = selectedInvoiceForLoss.payments as
						| PaymentDetail[]
						| null;
					const totalAllocated =
						payments?.reduce((sum, payment) => {
							const paymentValid =
								payment.payment_id !== null &&
								payment.payment_canceled_at === null &&
								payment.allocation_canceled_at === null;
							return paymentValid
								? sum + (payment.allocated_amount || 0)
								: sum;
						}, 0) || 0;
					return Math.max(
						0,
						(selectedInvoiceForLoss.invoice_value || 0) -
							totalAllocated
					);
				})()}
				loading={lossBookingLoading}
			/>
			{emailDialogData.invoice && (
				<InvoiceShareEmailDialog
					open={emailDialogData.open}
					onClose={resetEmailDialog}
					invoice={emailDialogData.invoice}
					connectedOrganizationEmail={emailDialogData.recipient}
				/>
			)}

			<InvoiceJobDocumentsDialog
				isOpen={jobDocumentsDialogOpen}
				onClose={() => {
					setJobDocumentsDialogOpen(false);
					setSelectedInvoiceForJobDocuments(null);
				}}
				invoice={selectedInvoiceForJobDocuments}
			/>
		</div>
	);
}
